import { Input } from '@apollo/orbit';
import { List } from '@apollo/space-kit/List';
import { ListDivider } from '@apollo/space-kit/ListDivider';
import { ListItem } from '@apollo/space-kit/ListItem';
import { Popover } from '@apollo/space-kit/Popover';
import React, { useEffect, useReducer, useRef } from 'react';

import { assertUnreachable } from 'src/lib/assertUnreachable';

import { PlainHtmlButton } from '../plainHtmlButton/PlainHtmlButton';

import { FilterResult, filter } from './fuzzy';

export function Typeahead<Option>({
  defaultValue,
  className,
  disabled = false,
  options: inputOptions,
  onSelected,
  renderToString,
  renderToListItem,
  renderDividerAfterCurrNode,
  placeholder,
  getSearchString,
  getId,
}: {
  defaultValue?: Option;
  className?: string;
  disabled?: boolean;
  options: Option[];
  placeholder?: string;
  onSelected: (value: Option) => void;
  renderToString: (value: Option) => string;
  renderToListItem: (
    highlightedSearchString: React.ReactNode,
    value: Option,
  ) => React.ReactNode;
  renderDividerAfterCurrNode?: (vallue: Option) => boolean;
  getSearchString: (value: Option) => string;
  getId: (value: Option) => string;
}) {
  const [{ options, search, selected, highlighted }, dispatch] = useReducer<
    React.Reducer<
      {
        options: FilterResult<(typeof inputOptions)[number]>[];
        highlighted?: (typeof inputOptions)[number];
        search?: string;
        selected?: (typeof inputOptions)[number];
      },
      | {
          type:
            | 'next'
            | 'previous'
            | 'select-active'
            | 'unselect'
            | 'unhighlight';
        }
      | { type: 'set-search'; search?: string }
      | { type: 'select'; id: string }
      | { type: 'highlight'; id: string }
    >
  >(
    (state, action) => {
      const activeI = state.highlighted
        ? state.options.findIndex((o) => o.original === state.highlighted)
        : -1;

      switch (action.type) {
        case 'next': {
          return {
            ...state,
            highlighted:
              state.options[(activeI + 1) % state.options.length]?.original,
          };
        }
        case 'previous': {
          return {
            ...state,
            highlighted:
              state.options[(activeI < 1 ? state.options.length : activeI) - 1]
                ?.original,
          };
        }
        case 'highlight': {
          return {
            ...state,
            highlighted: state.options.find(
              (o) => getId(o.original) === action.id,
            )?.original,
          };
        }
        case 'unhighlight':
          return {
            ...state,
            highlighted: undefined,
          };
        case 'set-search': {
          return {
            ...state,
            search: action.search,
            options: filter(action.search || '', inputOptions, (o) =>
              getSearchString(o),
            ),
          };
        }
        case 'select-active':
          if (onSelected && state.highlighted) onSelected(state.highlighted);
          return {
            ...state,
            highlighted: undefined,
            search: undefined,
            selected: state.highlighted,
            options: [],
          };
        case 'unselect':
          return {
            ...state,
            search: undefined,
            selected: undefined,
          };
        case 'select':
          if (onSelected && state.highlighted) onSelected(state.highlighted);
          return {
            ...state,
            highlighted: undefined,
            search: undefined,
            selected: state.options.find((o) => getId(o.original) === action.id)
              ?.original,
            options: [],
          };
        default:
          assertUnreachable(action);
      }
    },
    {
      options: [],
      highlighted: undefined,
      selected: defaultValue ?? undefined,
      search: undefined,
    },
  );

  const listRef = useRef<HTMLDivElement>(null);
  const activeElementRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (highlighted) {
      // This is the popover element that scrolls, getting this directly from a ref doesn't seem possible.
      const parentElement =
        listRef.current?.parentElement?.parentElement?.parentElement
          ?.parentElement;

      if (activeElementRef.current && parentElement) {
        const parentRect = parentElement.getBoundingClientRect();
        const focusedRect = activeElementRef.current.getBoundingClientRect();
        const overScroll = activeElementRef.current.offsetHeight / 3;

        if (focusedRect.bottom + overScroll > parentRect.bottom) {
          parentElement.scrollTop = Math.min(
            activeElementRef.current.offsetTop +
              activeElementRef.current.clientHeight -
              parentElement.offsetHeight +
              overScroll,
            parentElement.scrollHeight,
          );
        } else if (focusedRect.top - overScroll < parentRect.top) {
          parentElement.scrollTop = Math.max(
            activeElementRef.current.offsetTop - overScroll,
            0,
          );
        }
      }
    }
  }, [highlighted]);
  return (
    <Popover
      className={className}
      placement="bottom-start"
      content={
        <List ref={listRef}>
          {options.length > 0 ? (
            options.map(({ original, rendered }) => {
              return (
                <React.Fragment key={getId(original)}>
                  <ListItem
                    className="h-auto"
                    highlighted={highlighted === original}
                    selected={selected === original}
                    as={
                      <PlainHtmlButton
                        ref={
                          highlighted === original
                            ? activeElementRef
                            : undefined
                        }
                        className="w-full text-left"
                        onMouseEnter={() => {
                          if (original) {
                            dispatch({
                              type: 'highlight',
                              id: getId(original),
                            });
                          } else {
                            dispatch({ type: 'unhighlight' });
                          }
                        }}
                        onClick={() => {
                          if (original) {
                            dispatch({ type: 'select', id: getId(original) });
                          } else {
                            dispatch({ type: 'unselect' });
                          }
                        }}
                      />
                    }
                  >
                    {renderToListItem(rendered, original)}
                  </ListItem>
                  {renderDividerAfterCurrNode &&
                    renderDividerAfterCurrNode(original) && <ListDivider />}
                </React.Fragment>
              );
            })
          ) : (
            <div className="px-2">No results</div>
          )}
        </List>
      }
      triggerEvents="focusin"
      trigger={
        <Input
          className="flex-2"
          isDisabled={disabled}
          name="member"
          onChange={(event) => {
            dispatch({ type: 'set-search', search: event.target.value });
          }}
          placeholder={
            selected ? renderToString(selected) : (placeholder ?? 'Select')
          }
          spellCheck={false}
          value={search ?? (selected ? renderToString(selected) : '')}
          onFocus={() => {
            dispatch({ type: 'set-search', search: '' });
            if (selected) dispatch({ type: 'highlight', id: getId(selected) });
          }}
          autoComplete="off"
          onKeyDown={(event) => {
            switch (event.key) {
              case 'ArrowUp':
                dispatch({ type: 'previous' });
                break;
              case 'ArrowDown':
                dispatch({ type: 'next' });
                break;
              case 'Enter':
                dispatch({ type: 'select-active' });
                event.currentTarget.blur();
                break;
              default:
            }
          }}
        />
      }
    />
  );
}
